import history from '../history'
import {
  login,
  getUserAttributes,
  passwordReset,
  passwordResetWithCode,
  sendCode,
  authUser,
  signOutUser,
  getLongTermAccess,
  changePassword
} from '../library/aws'

const initialState = {
  authenticated: false,
  username: '',
  accountId: '',
  contactId: '',
  accountName: '',
  contactName: '',
  contactEmail: '',
  // Uploads tab: decided at login by stationportal/longtermaccess (Account Services Provided)
  showUploadsTab: false,
  uploadsAccessChecked: false,
  // Profile page: Cognito attributes of the signed-in user (read only) + change password form state
  profile: {
    attributes: {},
    loaded: false
  },
  changePassword: {
    loading: false,
    success: false,
    hasError: false,
    error: ''
  },
  user: {},
  login: {
    loading: false,
    password: '',
    success: false,
    error: '',
    hasError: false
  },
  passwordReset: {
    show: false,
    loading: false,
    success: false,
    error: '',
    hasError: false,
    newPassword: '',
    repeatPassword: '',
    verificationCode: '',
    resetWithCode: false
  },
  verificationCode: {
    show: false,
    loading: false,
    email: '',
    success: false,
    error: '',
    hasError: false
  }
}

const UPDATE_LOGIN = 'auth/UPDATE_LOGIN'
const UPDATE_RESET = 'auth/UPDATE_RESET'
const UPDATE_VERIFICATION_CODE = 'auth/UPDATE_VERIFICATION_CODE'
const UPDATE_AUTHENTICATED = 'auth/UPDATE_AUTHENTICATED'
const UPDATE_USER = 'auth/UPDATE_USER'
const UPDATE_USERNAME = 'auth/UPDATE_USERNAME'
const UPDATE_CONTACT_ID = 'auth/UPDATE_CONTACT_ID'
const UPDATE_ACCOUNT_ID = 'auth/UPDATE_ACCOUNT_ID'
const UPDATE_LONG_TERM_ACCESS = 'auth/UPDATE_LONG_TERM_ACCESS'
const UPDATE_PROFILE = 'auth/UPDATE_PROFILE'
const UPDATE_CHANGE_PASSWORD = 'auth/UPDATE_CHANGE_PASSWORD'

export const UPDATE_USER_ATTRIBUTES = 'auth/UPDATE_USER_ATTRIBUTES'

const handler = (state = initialState, action) => {
  const {payload} = action

  switch (action.type) {
    case UPDATE_AUTHENTICATED:
      return {
        ...state,
        authenticated: payload
      }

    case UPDATE_USER:
      return {
        ...state,
        user: payload
      }

    case UPDATE_USERNAME:
      return {
        ...state,
        username: payload
      }

    case UPDATE_LOGIN:
      return {
        ...state,
        login: {
          ...state.login,
          ...payload
        }
      }

    case UPDATE_RESET:
      return {
        ...state,
        passwordReset: {
          ...state.passwordReset,
          ...payload
        }
      }

    case UPDATE_VERIFICATION_CODE:
      return {
        ...state,
        verificationCode: {
          ...state.verificationCode,
          ...payload
        }
      }

    case UPDATE_ACCOUNT_ID:
      return {
        ...state,
        accountId: payload
      }

    case UPDATE_CONTACT_ID:
      return {
        ...state,
        contactId: payload
      }

    case UPDATE_PROFILE:
      return {
        ...state,
        profile: {
          ...state.profile,
          ...payload
        }
      }

    case UPDATE_CHANGE_PASSWORD:
      return {
        ...state,
        changePassword: {
          ...state.changePassword,
          ...payload
        }
      }

    case UPDATE_LONG_TERM_ACCESS:
      return {
        ...state,
        showUploadsTab: payload,
        uploadsAccessChecked: true
      }

    case UPDATE_USER_ATTRIBUTES:
      return {
        ...state,
        ...payload
      }

    default:
      return state
  }
}

export const authenticateUser = () => {
  return async(dispatch, getState) => {
    try {
      const {
        user,
        authenticated
      } = await authUser()

      dispatch({
        type: UPDATE_USER,
        payload: user
      })

      dispatch({
        type: UPDATE_AUTHENTICATED,
        payload: authenticated
      })

      const {
        'custom:PrimaryAccountSfid' :accountId,
        'custom:ContactSfid' :contactId
      } = await getUserAttributes(user)

      dispatch({
        type: UPDATE_ACCOUNT_ID,
        payload: accountId
      })

      dispatch({
        type: UPDATE_CONTACT_ID,
        payload: contactId
      })

      dispatch(requestLongTermAccess())

      if (history.location.pathname === '/login') {
        history.push('/')
      }
    }
    catch (e) {
      history.push('/login')
    }
  }
}

export const requestLogin = () => {
  return async(dispatch, getState) => {
    dispatch({
      type: UPDATE_LOGIN,
      payload: {
        loading: true,
        hasError: false,
        error: ''
      }
    })

    dispatch({
      type: UPDATE_RESET,
      payload: {
        loading: false,
        hasError: false,
        success: false,
        error: ''
      }
    })

    dispatch({
      type: UPDATE_VERIFICATION_CODE,
      payload: {
        loading: false,
        hasError: false,
        success: false,
        error: ''
      }
    })

    try {
      const {
        username
      } = getState().stores.auth

      const {
        password
      } = getState().stores.auth.login

      const loginAttempt = await login(username, password)

      const {
        authenticated,
        newPasswordRequired,
        user
      } = loginAttempt

      if(authenticated) {
        const {
          'custom:PrimaryAccountSfid' :accountId,
          'custom:ContactSfid' :contactId
        } = await getUserAttributes(user)

        dispatch({
          type: UPDATE_ACCOUNT_ID,
          payload: accountId
        })

        dispatch({
          type: UPDATE_CONTACT_ID,
          payload: contactId
        })
      }

      dispatch({
        type: UPDATE_AUTHENTICATED,
        payload: authenticated
      })

      if(authenticated) {
        dispatch({
          type: UPDATE_USER,
          payload: user
        })

        dispatch(requestLongTermAccess())
      }

      dispatch({
        type: UPDATE_RESET,
        payload: {
          show: newPasswordRequired
        }
      })

      if(user) {
        dispatch({
          type: UPDATE_USER,
          payload: user
        })
      }

      if(authenticated) {
        history.push('/')
      }
    }
    catch (e) {
      dispatch({
        type: UPDATE_LOGIN,
        payload: {
          hasError: true,
          error: e.message
        }
      })
    }

    dispatch({
      type: UPDATE_LOGIN,
      payload: {
        loading: false
      }
    })
  }
}

export const reportUsername = username => {
  return async dispatch => {
    dispatch({
      type: UPDATE_USERNAME,
      payload: username
    })
  }
}

export const reportPassword = password => {
  return async dispatch => {
    try {
      dispatch({
        type: UPDATE_LOGIN,
        payload: {
          password
        }
      })
    }
    catch (e) {
      dispatch({
        type: UPDATE_LOGIN,
        payload: {
          hasError: true,
          error: e.message
        }
      })
    }
  }
}

export const reportNewPassword = newPassword => {
  return async dispatch => {
    try {
      dispatch({
        type: UPDATE_RESET,
        payload: {
          newPassword
        }
      })
    }
    catch (e) {
      dispatch({
        type: UPDATE_RESET,
        payload: {
          hasError: true,
          error: e.message
        }
      })
    }
  }
}

export const reportRepeatPassword = repeatPassword => {
  return async dispatch => {
    try {
      dispatch({
        type: UPDATE_RESET,
        payload: {
          repeatPassword
        }
      })
    }
    catch (e) {
      dispatch({
        type: UPDATE_RESET,
        payload: {
          hasError: true,
          error: e.message
        }
      })
    }
  }
}

export const reportVerificationCode = code => {
  return async dispatch => {
    try {
      dispatch({
        type: UPDATE_RESET,
        payload: {
          verificationCode: code
        }
      })
    }
    catch (e) {
      dispatch({
        type: UPDATE_RESET,
        payload: {
          hasError: true,
          error: e.message
        }
      })
    }
  }
}

export const requestCloseOfResetPassword = () => {
  return async dispatch => {
    try {
      dispatch({
        type: UPDATE_RESET,
        payload: {
          loading: false,
          success: false,
          show: false,
          newPassword: '',
          repeatPassword: '',
          verificationCode: '',
          hasError: false,
          error: ''
        }
      })
    }
    catch (e) {
      dispatch({
        type: UPDATE_RESET,
        payload: {
          hasError: true,
          error: e.message
        }
      })
    }
  }
}

export const requestPasswordReset = () => {
  return async(dispatch, getState) => {
    try {
      dispatch({
        type: UPDATE_RESET,
        payload: {
          loading: true,
          success: false,
          hasError: false,
          error: ''
        }
      })

      const {
        user,
        username
      } = getState().stores.auth

      const {
        newPassword,
        repeatPassword,
        verificationCode,
        resetWithCode
      } = getState().stores.auth.passwordReset

      let isValid = true

      if (newPassword !== repeatPassword) {
        isValid = false

        dispatch({
          type: UPDATE_RESET,
          payload: {
            loading: false,
            hasError: true,
            error: 'Passwords don\'t match'
          }
        })
      }

      if (!username) {
        isValid = false

        dispatch({
          type: UPDATE_RESET,
          payload: {
            loading: false,
            hasError: true,
            error: 'Username is blank'
          }
        })
      }

      if (resetWithCode) {
        if (!verificationCode) {
          isValid = false

          dispatch({
            type: UPDATE_RESET,
            payload: {
              hasError: true,
              error: 'Verification Code is blank',
              loading: false
            }
          })
        }
      }

      if (resetWithCode) {
        if (isValid) {
          await passwordResetWithCode(username, newPassword, verificationCode)

          dispatch({
            type: UPDATE_LOGIN,
            payload: {
              password: newPassword
            }
          })

          dispatch({
            type: UPDATE_RESET,
            payload: {
              show: false,
              success: true,
              loading: false,
              newPassword: '',
              repeatPassword: '',
              verificationCode: '',
              hasError: false,
              error: ''
            }
          })

          dispatch({
            type: UPDATE_VERIFICATION_CODE,
            payload: {
              success: false,
              loading: false,
              newPassword: '',
              repeatPassword: '',
              verificationCode: '',
              hasError: false,
              error: ''
            }
          })
        }
      }

      if (!resetWithCode) {
        if (isValid) {
          await passwordReset(user, newPassword)

          dispatch({
            type: UPDATE_LOGIN,
            payload: {
              password: newPassword
            }
          })

          dispatch({
            type: UPDATE_RESET,
            payload: {
              show: false,
              success: true,
              loading: false,
              newPassword: '',
              repeatPassword: '',
              verificationCode: '',
              hasError: false,
              error: ''
            }
          })
        }
      }
    }
    catch (e) {
      dispatch({
        type: UPDATE_RESET,
        payload: {
          loading: false,
          hasError: true,
          error: e.message
        }
      })
    }
  }
}

export const requestShowRequestForVerificationCode = () => {
  return async(dispatch, getState) => {
    dispatch({
      type: UPDATE_VERIFICATION_CODE,
      payload: {
        show: true
      }
    })
  }
}

export const requestCloseRequestForVerificationCode = () => {
  return async(dispatch, getState) => {
    dispatch({
      type: UPDATE_VERIFICATION_CODE,
      payload: {
        show: false,
        hasError: false,
        error: ''
      }
    })
  }
}

export const requestSendVerificationCode = () => {
  return async(dispatch, getState) => {
    try {
      dispatch({
        type: UPDATE_VERIFICATION_CODE,
        payload: {
          loading: true,
          hasError: false,
          error: ''
        }
      })

      const {
        username
      } = getState().stores.auth

      let isValid = true

      if (!username) {
        isValid = false

        dispatch({
          type: UPDATE_VERIFICATION_CODE,
          payload: {
            loading: false,
            hasError: true,
            error: 'Username is blank'
          }
        })
      }

      if (isValid) {
        await sendCode(username)

        dispatch({
          type: UPDATE_VERIFICATION_CODE,
          payload: {
            show: false,
            success: true,
            loading: false,
            hasError: false,
            error: ''
          }
        })

        dispatch({
          type: UPDATE_RESET,
          payload: {
            show: true,
            success: true,
            resetWithCode: true,
            loading: false,
            hasError: false,
            error: ''
          }
        })
      }
    }
    catch (e) {
      dispatch({
        type: UPDATE_VERIFICATION_CODE,
        payload: {
          loading: false,
          hasError: true,
          error: e.message
        }
      })
    }
  }
}

export const requestShowPasswordResetWithCode = () => {
  return async(dispatch, getState) => {
    dispatch({
      type: UPDATE_RESET,
      payload: {
        show: true,
        resetWithCode: true
      }
    })
  }
}

export const requestSignOut = () => {
  return (dispatch, getState) => {
    signOutUser()

    dispatch({
      type: UPDATE_AUTHENTICATED,
      payload: false
    })

    dispatch({
      type: UPDATE_USER,
      payload: {}
    })

    dispatch({
      type: UPDATE_LONG_TERM_ACCESS,
      payload: false
    })

    history.push('/login')
  }
}

// Login-time check only: is the Uploads tab shown for this account?
// The tab's data (upload target, Versions, files) is loaded by stores/uploads when the tab is opened.
export const requestLongTermAccess = () => {
  return async(dispatch, getState) => {
    try {
      const {
        user
      } = getState().stores.auth

      const data = await getLongTermAccess(user)

      dispatch({
        type: UPDATE_LONG_TERM_ACCESS,
        payload: data.eligible === true
      })
    }
    catch (e) {
      console.error('[LONG TERM ACCESS]: ' + e)

      dispatch({
        type: UPDATE_LONG_TERM_ACCESS,
        payload: false
      })
    }
  }
}

// Profile page: read-only view of the Cognito attributes (name, email, phone, username)
export const requestProfile = () => {
  return async (dispatch, getState) => {
    try {
      const {user} = getState().stores.auth
      const attributes = await getUserAttributes(user)

      dispatch({
        type: UPDATE_PROFILE,
        payload: {
          attributes,
          loaded: true
        }
      })
    }
    catch (e) {
      console.error('[PROFILE]: ' + e)
      dispatch({
        type: UPDATE_PROFILE,
        payload: {
          loaded: true
        }
      })
    }
  }
}

export const requestChangePassword = (currentPassword, newPassword) => {
  return async (dispatch, getState) => {
    dispatch({
      type: UPDATE_CHANGE_PASSWORD,
      payload: {
        loading: true,
        success: false,
        hasError: false,
        error: ''
      }
    })

    try {
      const {user} = getState().stores.auth
      await changePassword(user, currentPassword, newPassword)

      dispatch({
        type: UPDATE_CHANGE_PASSWORD,
        payload: {
          loading: false,
          success: true
        }
      })
    }
    catch (e) {
      const friendly = e && e.code === 'NotAuthorizedException'
        ? 'The current password is incorrect.'
        : e && e.code === 'InvalidPasswordException'
          ? 'The new password does not meet the requirements.'
          : e && e.code === 'LimitExceededException'
            ? 'Too many attempts. Please wait a few minutes and try again.'
            : (e && e.message) || 'The password could not be changed.'

      dispatch({
        type: UPDATE_CHANGE_PASSWORD,
        payload: {
          loading: false,
          hasError: true,
          error: friendly
        }
      })
    }
  }
}

export const requestResetChangePassword = () => {
  return dispatch => {
    dispatch({
      type: UPDATE_CHANGE_PASSWORD,
      payload: {
        loading: false,
        success: false,
        hasError: false,
        error: ''
      }
    })
  }
}

export const requestNavigation = location => {
  return () => {
    history.push(location)
  }
}

export default handler